/**
 * Browser-side capsule bundle builder.
 *
 * Mirrors the CLI's build_capsule_bundle() in apps/cli/collect.py and the
 * extension's background.js — same schema (collect.capsule.v1), same hash
 * verification, same gzip+base64 wire format. Lets the web app send
 * capsules without a sidecar.
 */
import type { CapsuleBundle } from '../types';

export interface BundleBuildResult {
  bundle: CapsuleBundle;
  manifest: Record<string, unknown>;
  contentHash: string;     // hex sha256 of pre-gzip bundle
  contentSize: number;     // bytes of pre-gzip bundle
  payloadB64: string;      // gzip + base64
  rawSize: number;
  gzippedSize: number;
}

/** Largest file the web form takes (SendCapsule) and bundles; bigger files need the CLI. */
export const MAX_SOURCE_FILE_BYTES = 5_000_000;

const TEXT_EXTENSIONS = new Set([
  'md', 'markdown', 'txt', 'rst', 'org', 'log', 'csv', 'json', 'yaml', 'yml',
  'html', 'htm', 'xml', 'tsv', 'rtf',
]);

export function isLikelyText(file: File): boolean {
  if (file.type.startsWith('text/')) return true;
  if (file.type === 'application/json') return true;
  if (file.type === 'application/xml') return true;
  const ext = file.name.split('.').pop()?.toLowerCase();
  return ext ? TEXT_EXTENSIONS.has(ext) : false;
}

export function slugifyFilename(name: string): string {
  // keep extension, slugify base
  const idx = name.lastIndexOf('.');
  const base = idx > 0 ? name.slice(0, idx) : name;
  const ext = idx > 0 ? name.slice(idx + 1).toLowerCase() : 'md';
  const slug = base
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'file';
  // Always store as .md so the CLI's wiki conventions are respected
  // (single-file rule: each source is a markdown wrapper)
  return `${slug}.md`;
}

export async function buildBundleFromFiles(
  workspaceId: string,
  files: File[],
  opts: { purpose?: string } = {},
): Promise<BundleBuildResult> {
  if (files.length === 0) throw new Error('No files to bundle');

  const ts = new Date().toISOString().replace('T', ' ').slice(0, 19);
  const sources = await Promise.all(files.map(async (f) => {
    if (!isLikelyText(f)) {
      throw new Error(
        `${f.name} is not a text/markdown file (mime: ${f.type || 'unknown'}). ` +
        `For PDFs / DOCX / etc., use the CLI: \`python collect.py ingest <workspace> <file>\` ` +
        `which runs markitdown to convert first.`
      );
    }
    if (f.size > MAX_SOURCE_FILE_BYTES) {
      throw new Error(`${f.name} is larger than 5 MB. Split it or use the CLI.`);
    }
    const text = await f.text();
    const slug = slugifyFilename(f.name);
    const md = wrapAsSourceMd(f.name, text);
    return {
      path: `sources/${slug}`,
      size: md.length,
      content: md,
    };
  }));

  const bundle: CapsuleBundle = {
    schema: 'collect.capsule.v1',
    workspace: workspaceId,
    ts,
    purpose: opts.purpose || `Captures via Collect web app on ${new Date().toLocaleDateString()}.`,
    sources,
    summaries: [],
    concepts: [],
    kg_triples: [],
  };

  const bundleStr = JSON.stringify(bundle);
  const bundleBytes = new TextEncoder().encode(bundleStr);
  const contentSize = bundleBytes.length;

  // SHA-256
  const digest = await crypto.subtle.digest('SHA-256', bundleBytes);
  const contentHash = Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0')).join('');

  // gzip + base64
  const cs = new CompressionStream('gzip');
  const writer = cs.writable.getWriter();
  void writer.write(bundleBytes);
  void writer.close();
  const gz = new Uint8Array(await new Response(cs.readable).arrayBuffer());
  const payloadB64 = bytesToB64(gz);

  const manifest = {
    schema: 'collect.capsule.v1',
    workspace: workspaceId,
    ts,
    counts: {
      sources: sources.length,
      summaries: 0,
      concepts: 0,
      kg_triples: 0,
    },
    items: sources.map((s) => ({ type: 'source', path: s.path })),
  };

  return {
    bundle,
    manifest,
    contentHash,
    contentSize,
    payloadB64,
    rawSize: contentSize,
    gzippedSize: gz.length,
  };
}


function wrapAsSourceMd(filename: string, text: string): string {
  const now = new Date().toISOString();
  return `---
title: ${filename}
captured_at: ${now}
captured_via: collect-web
---

# ${filename}

> Captured ${now}

${text}
`;
}


function bytesToB64(bytes: Uint8Array): string {
  let bin = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    bin += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, Math.min(i + chunk, bytes.length))));
  }
  return btoa(bin);
}
